import { useTranslation } from '@hbs-us/ui';
import { useCombobox } from 'downshift';
import { useMemo, useState } from 'react';
import { useDebounced } from '../../lib/useDebounced.js';
import type { ClockInJob, SiteAddressSuggestion } from './contractsMirror.js';
import {
  MIN_QUERY_LENGTH,
  useBrowserPosition,
  useSiteSuggestionsQuery,
} from './useSiteSuggestions.js';

/**
 * "Where are you working?" — ONE field answering one question.
 *
 * DJ was offered a job `<select>` with a separate address input beneath it and declined it:
 * two controls answering one question still reads as a form. So assigned jobs and live address
 * matches share a single list, and what you pick decides what the punch carries:
 *
 *   a job        → `jobId`
 *   a suggestion → `siteAddress` + `siteLat` + `siteLng`   (soft-creates or reuses a site job)
 *   typed text   → `siteAddress` alone, behind a confirm   (the caller owns that dialog)
 *
 * The keyboard and ARIA plumbing is Downshift's. Everything below it — the two sections, the
 * position bias, the create affordance — is ours, because no library ships those.
 */

export type ClockTarget =
  | { kind: 'job'; job: ClockInJob }
  | { kind: 'site'; suggestion: SiteAddressSuggestion }
  | { kind: 'typed'; address: string };

/** The server rejects anything longer, so stop it here rather than at a 400. */
const MAX_ADDRESS_LENGTH = 200;

const FIELD =
  'h-11 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

const SECTION_LABEL =
  'px-3 pt-2 pb-1 font-semibold text-[0.68rem] text-muted-foreground uppercase tracking-wider';

export function labelForTarget(target: ClockTarget): string {
  if (target.kind === 'job') return target.job.jobName;
  if (target.kind === 'site') return target.suggestion.address;
  return target.address;
}

/**
 * A stable React key. NOT the array index: the list is rebuilt on every keystroke as suggestions
 * arrive, so an index key would re-map existing rows onto new items and carry highlight state
 * across them. Jobs key on their id because two sites can legitimately share a name.
 */
function keyForTarget(target: ClockTarget): string {
  if (target.kind === 'job') return `job-${target.job.id}`;
  if (target.kind === 'site') return `site-${target.suggestion.address}`;
  return 'typed';
}

export function SiteCombobox({
  jobs,
  disabled,
  onChange,
}: {
  jobs: ClockInJob[];
  disabled?: boolean;
  /** Fires with the chosen target, or null the moment the text stops matching it. */
  onChange: (target: ClockTarget | null) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const geo = useBrowserPosition();

  const trimmed = query.trim();
  const longEnough = trimmed.length >= MIN_QUERY_LENGTH;

  const suggestions = useSiteSuggestionsQuery(debounced, geo.position);
  const rows = suggestions.data?.suggestions ?? [];

  const matchingJobs = useMemo(() => {
    const needle = trimmed.toLowerCase();
    if (!needle) return jobs;
    return jobs.filter((job) => job.jobName.toLowerCase().includes(needle));
  }, [jobs, trimmed]);

  const items = useMemo<ClockTarget[]>(() => {
    const next: ClockTarget[] = matchingJobs.map((job) => ({ kind: 'job', job }));
    if (longEnough) {
      for (const suggestion of rows) next.push({ kind: 'site', suggestion });
      // The typed row is the escape hatch for a site Geoapify cannot find — rural lots, new
      // construction. It is suppressed when it would only duplicate something already listed,
      // so nobody creates "12 Elm St" twice from one screen.
      const duplicate =
        rows.some((r) => r.address.trim().toLowerCase() === trimmed.toLowerCase()) ||
        matchingJobs.some((j) => j.jobName.trim().toLowerCase() === trimmed.toLowerCase());
      if (!duplicate && trimmed.length <= MAX_ADDRESS_LENGTH) {
        next.push({ kind: 'typed', address: trimmed });
      }
    }
    return next;
  }, [matchingJobs, rows, longEnough, trimmed]);

  const firstSiteIndex = matchingJobs.length;
  const typedIndex = items.findIndex((item) => item.kind === 'typed');

  const combobox = useCombobox<ClockTarget>({
    items,
    inputValue: query,
    itemToString: (item) => (item ? labelForTarget(item) : ''),
    onInputValueChange: ({ inputValue, type }) => {
      const next = inputValue ?? '';
      setQuery(next);
      // ONLY a real keystroke invalidates the pick. Downshift reports an input-value change for
      // a SELECTION too, because choosing an item writes its label into the field — and it fires
      // that handler AFTER onSelectedItemChange (state key order is highlightedIndex, isOpen,
      // selectedItem, inputValue, and callOnChangeProps walks it in order). So an unguarded
      // `onChange(null)` here cleared the target one call after it was set, and the Clock in
      // button stayed disabled with the chosen site sitting visibly in the field. Nobody could
      // punch against an assigned job or a geocoded address at all.
      if (type !== useCombobox.stateChangeTypes.InputChange) return;

      // TYPING IS ITSELF A CHOICE. This used to be a bare `onChange(null)`, which meant the only
      // way to arm the Clock in button was to tap a row in the dropdown — and nothing on screen
      // said so. A crew member who typed their address in full sat looking at a greyed-out
      // button with the right text in the field and no way to learn what was missing. Worse, it
      // made the punch depend on a dropdown tap landing, which is the fragile part of this
      // screen on a phone.
      //
      // So enough typed text now stands on its own as a target, and the dropdown becomes a
      // shortcut rather than a toll gate. Nothing downstream gets laxer: a typed target still
      // goes through the caller's confirmation, and still through the duplicate-site check that
      // offers an existing job before a new one is created.
      const typed = next.trim();
      if (typed.length < MIN_QUERY_LENGTH) {
        onChange(null);
        return;
      }
      onChange({ kind: 'typed', address: typed });
      // The ONE moment a position is justified: they have started typing an address. Picking an
      // assigned job writes a long label into the field and must not be mistaken for typing.
      geo.request();
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem ?? null);
    },
  });

  const showApproxNotice = longEnough && (geo.status === 'denied' || geo.status === 'unavailable');

  return (
    <div className="w-full min-w-0">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: getLabelProps() sets htmlFor at runtime,
          which the linter cannot see through the spread. Proven in SiteCombobox.test.tsx —
          "associates its label with the input" resolves the field by its accessible name. */}
      <label className="font-medium text-foreground text-xs" {...combobox.getLabelProps()}>
        {t('timesheets:myClock.siteLabel')}
      </label>

      <div className="relative mt-1 w-full min-w-0">
        {/* No autoComplete here: Downshift's getInputProps sets it, and a duplicate loses. */}
        <input
          className={FIELD}
          disabled={disabled}
          maxLength={MAX_ADDRESS_LENGTH}
          {...combobox.getInputProps({
            placeholder: t('timesheets:myClock.sitePlaceholder'),
            // Downshift does NOT open on focus by default — it waits for a keystroke or an
            // ArrowDown. But the whole point of one merged field is that touching it shows the
            // jobs you are already assigned to, with no typing and no location prompt.
            onFocus: () => {
              if (!combobox.isOpen) combobox.openMenu();
            },
          })}
        />

        {/*
          `getMenuProps` must render on EVERY pass, open or not, or Downshift warns and the
          ref is lost. Emptiness is expressed by rendering no options inside it.
        */}
        <ul
          className={
            combobox.isOpen && items.length > 0
              ? 'absolute z-20 mt-1 max-h-72 w-full min-w-0 overflow-y-auto rounded-md border border-border bg-card py-1'
              : 'hidden'
          }
          {...combobox.getMenuProps()}
        >
          {combobox.isOpen &&
            items.map((item, index) => {
              const highlighted = combobox.highlightedIndex === index;
              const label = labelForTarget(item);
              return (
                <li key={keyForTarget(item)}>
                  {index === 0 && item.kind === 'job' ? (
                    <div className={SECTION_LABEL}>{t('timesheets:myClock.yourJobs')}</div>
                  ) : null}
                  {index === firstSiteIndex && item.kind === 'site' ? (
                    <div className={SECTION_LABEL}>{t('timesheets:myClock.addresses')}</div>
                  ) : null}
                  {index === typedIndex ? (
                    <div className="mt-1 border-border border-t pt-1" />
                  ) : null}

                  <div
                    className={`cursor-pointer px-3 py-2 text-sm ${
                      highlighted ? 'bg-secondary text-foreground' : 'text-foreground'
                    }`}
                    {...combobox.getItemProps({ item, index })}
                  >
                    {item.kind === 'typed' ? (
                      <>
                        <span>{t('timesheets:myClock.useTyped', { address: label })}</span>
                        <span className="mt-0.5 block text-muted-foreground text-xs">
                          {t('timesheets:myClock.startsNewSite')}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="block truncate">{label}</span>
                        {item.kind === 'site' ? (
                          <span className="mt-0.5 block text-muted-foreground text-xs">
                            {t('timesheets:myClock.startsNewSite')}
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
      </div>

      {/* Status lines. Rendered as text, never as the only way to learn something went wrong. */}
      {suggestions.isFetching ? (
        <p className="mt-1 text-muted-foreground text-xs">{t('timesheets:myClock.searching')}</p>
      ) : null}

      {showApproxNotice ? (
        <p className="mt-1 text-muted-foreground text-xs">
          {t('timesheets:myClock.locationOff')}{' '}
          <button
            type="button"
            className="underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => geo.request()}
          >
            {t('timesheets:myClock.useMyLocation')}
          </button>
        </p>
      ) : null}
    </div>
  );
}
